import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Filter20Regular,
    ArrowSort20Regular,
    Grid20Regular,
    EyeOff20Regular,
    ChevronDown12Regular,
    ChevronDown20Regular,
    MoreVertical16Regular,
    AddCircle24Filled,
    ArrowExport20Regular,
    ArrowDownload20Regular,
    Checkmark16Regular,
    Comment16Filled,
    CommentAdd16Regular,
    Dismiss16Regular,
    Table20Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider } from '@fluentui/react-components';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { toast } from 'react-toastify';
import styles from '../TaskManagement/css/index.module.css';
import { CustomAvatar } from '../TaskManagement';
import ResizableColumnHeader from '../TaskManagement/ResizableColumnHeader';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import ActionLogDetailsSidebar from './ActionLogDetailsSidebar';
import { getGroupBySettings, saveGroupBySettings } from '../../utils/groupByPersistence';
import {
    GetActionLogs,
    PostActionLog,
    PatchActionLog,
    DeleteActionLog,
    GetProjectActionLogWorkstreams,
    PostProjectActionLogWorkstream,
    GetProjectActionLogSources,
    PostProjectActionLogSource,
    ExportActionLogCSV,
    DownloadActionLogTemplate,
    UploadActionLogCSV,
    BulkUpdateActionLogs,
    BulkDeleteActionLogs,
} from '../../redux/actionLogSlice';
import { GetProjectRaidOwners } from '../../redux/raidSlice';
import { GetWorkstreams } from '../../redux/dataroomSlice';

const SortableHeaderCell = ({ id, width, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
        transition,
        opacity: isDragging ? 0.6 : 1,
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        boxSizing: 'border-box',
        padding: 0,
        '--column-width': `${width}px`,
        cursor: isDragging ? 's-resize' : undefined,
    };
    return (
        <th ref={setNodeRef} style={style} className={`${styles.headerRowItem} ${isDragging ? 'isDragging' : ''}`} data-sortable-id={id} {...attributes} {...listeners}>
            <div style={{ width: '100%' }}>{children}</div>
        </th>
    );
};

// Minimal CSV header parser (same approach as Task Management)
const parseCsvHeader = line => {
    const headers = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        const next = line[i + 1];
        if (ch === '"' && inQuotes && next === '"') {
            current += '"';
            i += 1;
            continue;
        }
        if (ch === '"') {
            inQuotes = !inQuotes;
            continue;
        }
        if (ch === ',' && !inQuotes) {
            headers.push(current.trim());
            current = '';
            continue;
        }
        current += ch;
    }
    headers.push(current.trim());
    return headers.filter(Boolean);
};

const ActionLog = () => {
    const dispatch = useDispatch();
    const { actionLogs, status, buttonLoading, exportCSVLoading, downloadTemplateLoading, uploadCSVBtnLoading, workstreams, sources } = useSelector(
        state =>
            state.actionLog || {
                actionLogs: [],
                status: 'idle',
                buttonLoading: false,
                exportCSVLoading: false,
                downloadTemplateLoading: false,
                uploadCSVBtnLoading: false,
                workstreams: [],
                sources: [],
            },
    );
    const { projectRaidOwners } = useSelector(state => state.raid || { projectRaidOwners: [] });
    const { selectedProject } = useSelector(state => state.projectData);
    const { projectUsersAll } = useSelector(state => state.team || { projectUsersAll: [] });
    const { workstreams: dataroomWorkstreams } = useSelector(state => state.dataroom || { workstreams: [] });

    const [columnWidths, setColumnWidths] = React.useState({
        id: 60,
        action: 270,
        owner: 80,
        dueDate: 120,
        status: 140,
        workstream: 160,
        source: 180,
        loggedOn: 140,
        updatedOn: 120,
        actions: 30,
    });

    const defaultColumnOrder = ['action', 'owner', 'dueDate', 'status', 'workstream', 'source', 'loggedOn', 'updatedOn'];
    const [columnOrder, setColumnOrder] = React.useState(defaultColumnOrder);

    const [showAllColumns, setShowAllColumns] = React.useState(true);
    const [hiddenColumns, setHiddenColumns] = React.useState([]);

    const [filters, setFilters] = React.useState([{ column: '', value: '', id: Date.now() }]);
    const [sortColumn, setSortColumn] = React.useState('');
    const [sortOrder, setSortOrder] = React.useState('');

    const [groupByColumn, setGroupByColumn] = React.useState('');
    const [groupBySort, setGroupBySort] = React.useState('');
    const [collapsedGroups, setCollapsedGroups] = React.useState(new Set());

    const [newActionText, setNewActionText] = React.useState('');
    const [highlightAddRow, setHighlightAddRow] = React.useState(false);
    const addRowRef = React.useRef(null);
    const newActionInputRef = React.useRef(null);
    const tableContainerRef = React.useRef(null);
    const csvUploadInputId = 'csv-upload-action-log';

    const [editingActionId, setEditingActionId] = React.useState(null);
    const [editingActionText, setEditingActionText] = React.useState('');

    const [ownerMenuOpenRowId, setOwnerMenuOpenRowId] = React.useState(null);
    const [workstreamMenuOpenRowId, setWorkstreamMenuOpenRowId] = React.useState(null);
    const [sourceMenuOpenRowId, setSourceMenuOpenRowId] = React.useState(null);
    const [newWorkstreamName, setNewWorkstreamName] = React.useState('');
    const [newSourceName, setNewSourceName] = React.useState('');

    const [selectedRows, setSelectedRows] = React.useState([]);
    const [hoveredRowId, setHoveredRowId] = React.useState(null);
    const [hoveredWorkstreamId, setHoveredWorkstreamId] = React.useState(null);
    const [hoveredSourceId, setHoveredSourceId] = React.useState(null);
    const [headerIdHovered, setHeaderIdHovered] = React.useState(false);
    const [selectedRowForDelete, setSelectedRowForDelete] = React.useState(null);
    const deleteModalRef = React.useRef(null);
    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const [selectedEntryForSidebar, setSelectedEntryForSidebar] = React.useState(null);
    const [csvFileForMapping, setCsvFileForMapping] = React.useState(null);
    const [csvHeaders, setCsvHeaders] = React.useState([]);
    const [csvMapping, setCsvMapping] = React.useState({
        action_text: '',
        owner: '',
        due_date: '',
        status: '',
        workstream_repr: '',
        source_repr: '',
    });

    const handleColumnResize = (key, w) => setColumnWidths(prev => ({ ...prev, [key]: w }));

    const isColumnVisible = col => (showAllColumns ? true : !hiddenColumns.includes(col));
    const handleShowAllColumns = checked => {
        if (checked) {
            setShowAllColumns(true);
            setHiddenColumns([]);
        } else setShowAllColumns(false);
    };
    const handleColumnVisibility = (column, checked) => {
        if (column === 'showAll') {
            handleShowAllColumns(checked);
            return;
        }
        if (checked) {
            setHiddenColumns(prev => prev.filter(c => c !== column));
        } else {
            setHiddenColumns(prev => [...prev, column]);
            setShowAllColumns(false);
        }
    };

    React.useEffect(() => {
        if (selectedProject?.id) {
            dispatch(GetActionLogs());
            dispatch(GetProjectRaidOwners());
            dispatch(GetProjectActionLogWorkstreams());
            dispatch(GetProjectActionLogSources());
            dispatch(GetWorkstreams());
        }
    }, [dispatch, selectedProject?.id]);

    React.useEffect(() => {
        if (selectedProject?.id) {
            const saved = getGroupBySettings(`${selectedProject.id}_action_log`);
            if (saved) {
                setGroupByColumn(saved.column || '');
                setGroupBySort(saved.sort || '');
                setCollapsedGroups(new Set(saved.collapsedGroups || []));
            } else {
                setGroupByColumn('');
                setGroupBySort('');
                setCollapsedGroups(new Set());
            }
        }
    }, [selectedProject?.id]);

    React.useEffect(() => {
        if (!selectedProject?.id) return;
        saveGroupBySettings(`${selectedProject.id}_action_log`, {
            column: groupByColumn,
            sort: groupBySort,
            collapsedGroups: Array.from(collapsedGroups),
        });
    }, [selectedProject?.id, groupByColumn, groupBySort, collapsedGroups]);

    const allOwners = React.useMemo(() => {
        const m = new Map();
        (projectRaidOwners || []).forEach(o => o?.name && m.set(o.name, o.name));
        (projectUsersAll || []).forEach(u => {
            const n = u?.value?.full_name;
            if (n) m.set(n, n);
        });
        (actionLogs || []).forEach(r => r?.owner && m.set(r.owner, r.owner));
        return Array.from(m.keys());
    }, [projectRaidOwners, projectUsersAll, actionLogs]);

    const allWorkstreamNames = React.useMemo(() => {
        const m = new Map();
        (dataroomWorkstreams || []).forEach(ws => ws?.name && m.set(ws.name, ws));
        (workstreams || []).forEach(ws => ws?.name && m.set(ws.name, ws));
        (actionLogs || []).forEach(r => {
            if (r?.workstream_repr && !m.has(r.workstream_repr)) {
                m.set(r.workstream_repr, { id: `row_${r.workstream_repr}`, name: r.workstream_repr });
            }
        });
        return Array.from(m.values());
    }, [dataroomWorkstreams, workstreams, actionLogs]);

    const allSourceNames = React.useMemo(() => {
        const m = new Map();
        (sources || []).forEach(s => s?.name && m.set(s.name, s));
        (actionLogs || []).forEach(r => {
            if (r?.source_repr && !m.has(r.source_repr)) {
                m.set(r.source_repr, { id: `row_${r.source_repr}`, name: r.source_repr });
            }
        });
        return Array.from(m.values());
    }, [sources, actionLogs]);

    const getStatusInfo = statusCode => {
        const map = {
            NOT_STARTED: { bg: '#C0C6D8', text: 'Not Started' },
            WORKING_ON_IT: { bg: 'var(--RAG-Amber)', text: 'Working on it' },
            COMPLETED: { bg: 'var(--RAG-Green)', text: 'Completed' },
            STUCK: { bg: 'var(--RAG-Red)', text: 'Stuck' },
        };
        return map[statusCode] || { bg: '#C0C6D8', text: '—' };
    };

    const formatDate = d => {
        if (!d) return '-- --';
        try {
            return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
            return '-- --';
        }
    };

    const formatLastUpdated = ts => {
        if (!ts) return '-- --';
        try {
            const date = new Date(ts);
            const now = new Date();
            const diffInMs = now - date;
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

            if (diffInMinutes < 1) return 'just now';
            if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
            if (diffInHours < 24) return `${diffInHours} hrs ago`;

            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch {
            return '-- --';
        }
    };

    const isRowSelected = id => selectedRows.includes(id);
    const toggleRowSelected = id => setSelectedRows(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    const handleAddWorkstream = async () => {
        const n = newWorkstreamName.trim();
        if (!n) return;
        await dispatch(PostProjectActionLogWorkstream({ name: n }));
        setNewWorkstreamName('');
    };

    const handleAddSourceLabel = async () => {
        const n = newSourceName.trim();
        if (!n) return;
        await dispatch(PostProjectActionLogSource({ name: n }));
        setNewSourceName('');
    };

    const handleSourceSelect = (row, name) => {
        const next = name === row.source_repr ? null : name;
        dispatch(PatchActionLog({ id: row.id, body: { source_repr: next || null } }));
        setSourceMenuOpenRowId(null);
    };

    const handleFilterColumnOptions = () => [
        { value: 'status', label: 'Status' },
        { value: 'owner', label: 'Owner' },
        { value: 'workstream', label: 'Workstream' },
        { value: 'source', label: 'Source' },
    ];

    const handleSortColumnOptions = () => [
        { value: 'action', label: 'Action' },
        { value: 'owner', label: 'Owner' },
        { value: 'dueDate', label: 'Due date' },
        { value: 'status', label: 'Status' },
        { value: 'workstream', label: 'Workstream' },
        { value: 'loggedOn', label: 'Logged on' },
        { value: 'updatedOn', label: 'Updated on' },
    ];

    const getFilterValueOptions = column => {
        const items = actionLogs || [];
        if (!column) return [];
        const add = (acc, v, label) => {
            if (v == null || v === '') return acc;
            if (!acc.find(o => o.value === v)) acc.push({ value: v, label: label || v });
            return acc;
        };
        let acc = [];
        switch (column) {
            case 'status':
                items.forEach(r => add(acc, r.status, getStatusInfo(r.status).text));
                return acc;
            case 'owner':
                items.forEach(r => add(acc, r.owner, r.owner));
                return acc;
            case 'workstream':
                items.forEach(r => add(acc, r.workstream_repr, r.workstream_repr));
                return acc;
            case 'source':
                items.forEach(r => add(acc, r.source_repr, r.source_repr));
                return acc;
            default:
                return [];
        }
    };

    const applyFilters = React.useCallback(
        items => {
            if (!items?.length || !filters?.length) return items || [];
            return items.filter(item =>
                filters.every(f => {
                    if (!f.column || !f.value) return true;
                    switch (f.column) {
                        case 'status':
                            return item.status === f.value;
                        case 'owner':
                            return item.owner === f.value;
                        case 'workstream':
                            return item.workstream_repr === f.value;
                        case 'source':
                            return item.source_repr === f.value;
                        default:
                            return true;
                    }
                }),
            );
        },
        [filters],
    );

    const applySorting = React.useCallback(
        items => {
            if (!items?.length || !sortColumn || !sortOrder) return items || [];
            const sorted = [...items].sort((a, b) => {
                const get = row => {
                    switch (sortColumn) {
                        case 'action':
                            return (row.action_text || '').toLowerCase();
                        case 'owner':
                            return (row.owner || '').toLowerCase();
                        case 'dueDate':
                            return row.due_date ? new Date(row.due_date).getTime() : 0;
                        case 'status':
                            return (row.status || '').toLowerCase();
                        case 'workstream':
                            return (row.workstream_repr || '').toLowerCase();
                        case 'loggedOn':
                            return row.created ? new Date(row.created).getTime() : 0;
                        case 'updatedOn':
                            return row.last_updated ? new Date(row.last_updated).getTime() : 0;
                        default:
                            return '';
                    }
                };
                const av = get(a);
                const bv = get(b);
                if (av === bv) return 0;
                const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
                return sortOrder === 'asc' ? cmp : -cmp;
            });
            return sorted;
        },
        [sortColumn, sortOrder],
    );

    const applyGrouping = items => {
        if (!items?.length || !groupByColumn) return null;
        const groups = {};
        items.forEach(item => {
            let key = '-- --';
            switch (groupByColumn) {
                case 'status':
                    key = getStatusInfo(item.status).text;
                    break;
                case 'owner':
                    key = item.owner || '-- --';
                    break;
                case 'workstream':
                    key = item.workstream_repr || '-- --';
                    break;
                case 'source':
                    key = item.source_repr || '-- --';
                    break;
                default:
                    key = 'Other';
            }
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        });
        let entries = Object.entries(groups).map(([groupName, groupItems]) => ({ groupName, items: groupItems }));
        if (groupBySort === 'asc') entries.sort((a, b) => a.groupName.localeCompare(b.groupName));
        else if (groupBySort === 'desc') entries.sort((a, b) => b.groupName.localeCompare(a.groupName));
        return entries;
    };

    const filteredSorted = React.useMemo(() => applySorting(applyFilters(actionLogs && actionLogs.length ? actionLogs : [])), [actionLogs, applyFilters, applySorting]);

    const visibleIds = React.useMemo(() => (filteredSorted || []).map(r => r.id), [filteredSorted]);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedRows.includes(id));
    const handleToggleSelectAllVisible = () => {
        if (allVisibleSelected) setSelectedRows(prev => prev.filter(id => !visibleIds.includes(id)));
        else setSelectedRows(prev => Array.from(new Set([...prev, ...visibleIds])));
    };

    const handleCreateAction = async () => {
        const t = newActionText.trim();
        if (!t) return;
        await dispatch(PostActionLog({ action_text: t }));
        setNewActionText('');
    };

    // CSV actions (templated export/import)
    const handleExportSelectedActionLogCSV = () => {
        if (exportCSVLoading || selectedRows.length === 0) return;
        dispatch(ExportActionLogCSV(selectedRows));
    };

    const handleImportActionLogCSV = () => {
        const input = document.getElementById(csvUploadInputId);
        if (input && !uploadCSVBtnLoading) input.click();
    };

    const handleDownloadActionLogTemplate = () => {
        if (!downloadTemplateLoading) dispatch(DownloadActionLogTemplate());
    };

    const handleActionLogFileUpload = event => {
        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name?.toLowerCase() || '';

        // Keep direct upload for xlsx/xls, same as existing PM behavior.
        if (!fileName.endsWith('.csv')) {
            if (!uploadCSVBtnLoading) {
                const formData = new FormData();
                formData.append('file', file);
                dispatch(UploadActionLogCSV(formData));
            }
            event.target.value = '';
            return;
        }

        // CSV: open column-mapping modal using parsed header row.
        const reader = new FileReader();
        reader.onload = e => {
            try {
                const text = (e.target?.result || '').toString();
                const firstNonEmptyLine = text.split(/\r?\n/).find(line => line.trim().length > 0) || '';
                if (!firstNonEmptyLine) {
                    toast.error('No headers found in CSV file.');
                    return;
                }

                const headers = parseCsvHeader(firstNonEmptyLine);
                if (!headers.length) {
                    toast.error('No headers found in CSV file.');
                    return;
                }

                const normalizedHeaders = headers.map(h => (h || '').toString().trim());
                const fieldAliases = {
                    action_text: ['action', 'action text', 'task', 'task name', 'item', 'description'],
                    owner: ['owner', 'assigned to', 'assignee'],
                    due_date: ['due date', 'duedate', 'deadline', 'target date', 'end date'],
                    status: ['status', 'state', 'current status'],
                    workstream_repr: ['workstream', 'stream', 'work stream'],
                    source_repr: ['source', 'tag', 'origin'],
                };

                const guessedMapping = {
                    action_text: '',
                    owner: '',
                    due_date: '',
                    status: '',
                    workstream_repr: '',
                    source_repr: '',
                };

                const findBestMatch = aliases => {
                    const loweredAliases = aliases.map(a => a.toLowerCase());
                    for (const header of normalizedHeaders) {
                        if (loweredAliases.includes(header.toLowerCase())) return header;
                    }
                    for (const header of normalizedHeaders) {
                        const h = header.toLowerCase();
                        if (loweredAliases.some(a => h.includes(a))) return header;
                    }
                    return '';
                };

                Object.keys(fieldAliases).forEach(fieldKey => {
                    const match = findBestMatch(fieldAliases[fieldKey]);
                    if (match) guessedMapping[fieldKey] = match;
                });

                setCsvFileForMapping(file);
                setCsvHeaders(headers);
                setCsvMapping(prev => ({
                    ...prev,
                    ...guessedMapping,
                    action_text: guessedMapping.action_text || prev.action_text || headers[0],
                }));
            } catch (err) {
                console.error('Failed to read CSV file:', err);
                toast.error('Failed to read CSV file. Please check the file format.');
            }
        };
        reader.onerror = () => toast.error('Failed to read CSV file. Please check the file format.');
        reader.readAsText(file);
        event.target.value = '';
    };

    const handleChangeCsvMapping = (fieldKey, headerName) => {
        setCsvMapping(prev => ({ ...prev, [fieldKey]: headerName }));
    };

    const handleConfirmCsvImport = () => {
        if (!csvFileForMapping) return;
        if (!csvMapping.action_text) {
            toast.error('Please map at least the Action column before importing.');
            return;
        }
        const formData = new FormData();
        formData.append('file', csvFileForMapping);
        formData.append('column_mapping', JSON.stringify(csvMapping));
        dispatch(UploadActionLogCSV(formData));

        setCsvFileForMapping(null);
        setCsvHeaders([]);
        setCsvMapping({
            action_text: '',
            owner: '',
            due_date: '',
            status: '',
            workstream_repr: '',
            source_repr: '',
        });
    };

    const handleCancelCsvImport = () => {
        setCsvFileForMapping(null);
        setCsvHeaders([]);
        setCsvMapping({
            action_text: '',
            owner: '',
            due_date: '',
            status: '',
            workstream_repr: '',
            source_repr: '',
        });
    };

    const handleAddRowClick = () => {
        requestAnimationFrame(() => {
            tableContainerRef.current?.scrollTo?.(0, tableContainerRef.current.scrollHeight);
            addRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            newActionInputRef.current?.focus();
            setHighlightAddRow(true);
            setTimeout(() => setHighlightAddRow(false), 1000);
        });
    };

    const startEditAction = (e, row) => {
        e.stopPropagation();
        setEditingActionId(row.id);
        setEditingActionText(row.action_text || '');
    };

    const saveEditAction = row => {
        if (!editingActionId) return;
        const t = editingActionText.trim();
        if (t !== (row.action_text || '')) dispatch(PatchActionLog({ id: row.id, body: { action_text: t } }));
        setEditingActionId(null);
        setEditingActionText('');
    };

    const cancelEditAction = () => {
        setEditingActionId(null);
        setEditingActionText('');
    };

    const labelMap = {
        action: 'Action',
        owner: 'Owner',
        dueDate: 'Due date',
        status: 'Status',
        workstream: 'Workstream',
        source: 'Source',
        loggedOn: 'Logged on',
        updatedOn: 'Updated on',
    };

    const renderBodyCell = (row, col) => {
        switch (col) {
            case 'action':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.action }}>
                        {editingActionId === row.id ? (
                            <input
                                type="text"
                                className={'form-control form-control-sm ' + styles.taskTextField}
                                value={editingActionText}
                                onChange={e => setEditingActionText(e.target.value)}
                                onBlur={() => saveEditAction(row)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') saveEditAction(row);
                                    if (e.key === 'Escape') cancelEditAction();
                                }}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div className="d-flex align-items-center">
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                    <div
                                        className={styles.taskText}
                                        title={row.action_text || ''}
                                        onClick={e => startEditAction(e, row)}
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: '100%',
                                            color: row.action_text ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)',
                                        }}>
                                        {row.action_text || '-- --'}
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <div
                                        className={styles.commentIcon}
                                        onClick={() => {
                                            setSelectedEntryForSidebar(row);
                                            setSidebarOpen(true);
                                        }}
                                        style={{ cursor: 'pointer' }}>
                                        {row?.comments_count > 0 ? <Comment16Filled color="var(--Brand-Secondary)" /> : <CommentAdd16Regular />}
                                    </div>
                                </div>
                            </div>
                        )}
                    </td>
                );
            case 'owner':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.owner }}>
                        <Menu open={ownerMenuOpenRowId === row.id} onOpenChange={(e, data) => setOwnerMenuOpenRowId(data.open ? row.id : null)}>
                            <MenuTrigger>
                                <div
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <CustomAvatar name={row.owner || ''} size={20} color={row.owner ? 'var(--Brand-Primary)' : '#6c757d'} />
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '232px', maxHeight: '400px', overflowY: 'auto' }}>
                                <MenuList>
                                    {allOwners.map(name => (
                                        <div
                                            key={name}
                                            onClick={() => {
                                                dispatch(PatchActionLog({ id: row.id, body: { owner: name } }));
                                                setOwnerMenuOpenRowId(null);
                                            }}
                                            className={styles.tagMenuItem}>
                                            <span>{name}</span>
                                        </div>
                                    ))}
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'dueDate':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.dueDate }}>
                        <input
                            type="date"
                            className={'form-control form-control-sm ' + styles.taskTextField}
                            value={row.due_date ? String(row.due_date).slice(0, 10) : ''}
                            onChange={e => dispatch(PatchActionLog({ id: row.id, body: { due_date: e.target.value || null } }))}
                        />
                    </td>
                );
            case 'status':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.status }}>
                        <Menu>
                            <MenuTrigger>
                                <div
                                    className={styles.statusDiv}
                                    style={{
                                        backgroundColor: getStatusInfo(row.status).bg,
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}>
                                    {getStatusInfo(row.status).text}
                                </div>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    {['NOT_STARTED', 'WORKING_ON_IT', 'COMPLETED', 'STUCK'].map(code => (
                                        <MenuItem
                                            key={code}
                                            className={styles.statusMenuItem}
                                            onClick={() => dispatch(PatchActionLog({ id: row.id, body: { status: code } }))}
                                            style={{ backgroundColor: getStatusInfo(code).bg, color: 'white' }}>
                                            {getStatusInfo(code).text}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'workstream':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.workstream }}>
                        <Menu open={workstreamMenuOpenRowId === row.id} onOpenChange={(e, data) => setWorkstreamMenuOpenRowId(data.open ? row.id : null)}>
                            <MenuTrigger>
                                <div
                                    style={{
                                        width: '100%',
                                        minHeight: 32,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <span className={styles.tableRegularText} style={{ color: row.workstream_repr ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)' }}>
                                        {row.workstream_repr || '-- --'}
                                    </span>
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: 240 }}>
                                <MenuList>
                                    {allWorkstreamNames.map(ws =>
                                        (() => {
                                            const key = ws.id || ws.name;
                                            const isSelected = row.workstream_repr === ws.name;
                                            const isHovered = hoveredWorkstreamId === key;
                                            const showCross = isSelected && isHovered;
                                            const showCheck = isSelected && !isHovered;
                                            return (
                                                <div
                                                    key={key}
                                                    className={styles.tagMenuItem}
                                                    onMouseEnter={() => setHoveredWorkstreamId(key)}
                                                    onMouseLeave={() => setHoveredWorkstreamId(null)}
                                                    style={
                                                        isSelected
                                                            ? {
                                                                  backgroundColor: 'rgba(142, 45, 226, 0.05)',
                                                              }
                                                            : undefined
                                                    }
                                                    onClick={() => {
                                                        const nextWorkstream = row.workstream_repr === ws.name ? null : ws.name;
                                                        dispatch(PatchActionLog({ id: row.id, body: { workstream_repr: nextWorkstream } }));
                                                        setWorkstreamMenuOpenRowId(null);
                                                    }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                            gap: 8,
                                                        }}>
                                                        <span>{ws.name}</span>
                                                        <>
                                                            {showCheck && <Checkmark16Regular style={{ color: 'var(--Brand-Secondary)' }} />}
                                                            {showCross && <Dismiss16Regular style={{ color: 'var(--Brand-Secondary)' }} />}
                                                        </>
                                                    </div>
                                                </div>
                                            );
                                        })(),
                                    )}
                                    <MenuDivider />
                                    <div className="p-2 d-flex gap-2 align-items-center">
                                        <input
                                            className={styles.tagInput}
                                            placeholder="New workstream"
                                            value={newWorkstreamName}
                                            onChange={e => setNewWorkstreamName(e.target.value)}
                                        />
                                        <AddCircle24Filled style={{ cursor: 'pointer' }} onClick={handleAddWorkstream} />
                                    </div>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'source':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.source }}>
                        <Menu open={sourceMenuOpenRowId === row.id} onOpenChange={(e, data) => setSourceMenuOpenRowId(data.open ? row.id : null)}>
                            <MenuTrigger>
                                <div
                                    style={{
                                        width: '100%',
                                        minHeight: 32,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 4px',
                                    }}>
                                    <span
                                        className={styles.tableRegularText}
                                        style={{
                                            color: row.source_repr ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {row.source_repr || '-- --'}
                                    </span>
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: 260, maxHeight: 360, overflowY: 'auto' }}>
                                <MenuList>
                                    {/* <MenuItem onClick={() => dispatch(PatchActionLog({ id: row.id, body: { source_repr: null } }))}>-- --</MenuItem> */}
                                    {allSourceNames.map(s =>
                                        (() => {
                                            const key = s.id || s.name;
                                            const isSelected = row.source_repr === s.name;
                                            const isHovered = hoveredSourceId === key;
                                            const showCross = isSelected && isHovered;
                                            const showCheck = isSelected && !isHovered;
                                            return (
                                                <div
                                                    key={key}
                                                    className={styles.tagMenuItem}
                                                    onMouseEnter={() => setHoveredSourceId(key)}
                                                    onMouseLeave={() => setHoveredSourceId(null)}
                                                    style={
                                                        isSelected
                                                            ? {
                                                                  backgroundColor: 'rgba(142, 45, 226, 0.05)',
                                                              }
                                                            : undefined
                                                    }
                                                    onClick={() => handleSourceSelect(row, s.name)}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                            gap: 8,
                                                        }}>
                                                        <span>{s.name}</span>
                                                        <>
                                                            {showCheck && <Checkmark16Regular style={{ color: 'var(--Brand-Secondary)' }} />}
                                                            {showCross && <Dismiss16Regular style={{ color: 'var(--Brand-Secondary)' }} />}
                                                        </>
                                                    </div>
                                                </div>
                                            );
                                        })(),
                                    )}
                                    <MenuDivider />
                                    <div className="p-2 d-flex gap-2 align-items-center">
                                        <input className={styles.tagInput} placeholder="New source" value={newSourceName} onChange={e => setNewSourceName(e.target.value)} />
                                        <AddCircle24Filled style={{ cursor: 'pointer' }} onClick={handleAddSourceLabel} />
                                    </div>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'loggedOn':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.loggedOn }}>
                        <div className="d-flex align-items-center gap-2 px-2 justify-content-center">
                            <CustomAvatar margin="ms-0 me-2" name={row.created_by_name || ''} size={20} />
                            <span className={styles.tableRegularText} style={{ width: 72 }}>
                                {formatDate(row.created)}
                            </span>
                        </div>
                    </td>
                );
            case 'updatedOn':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.updatedOn }}>
                        <div className="d-flex align-items-center gap-2 justify-content-center px-2">
                            <CustomAvatar margin="ms-2 me-2" name={row.last_updated_by_name || ''} size={20} />
                            <span className={styles.tableRegularText}>{formatLastUpdated(row.last_updated)}</span>
                        </div>
                    </td>
                );
            default:
                return null;
        }
    };

    const renderIdCell = (row, index) => {
        const showCb = isRowSelected(row.id) || hoveredRowId === row.id;
        if (showCb) {
            return (
                <input
                    type="checkbox"
                    className={styles.checkboxTable}
                    checked={isRowSelected(row.id)}
                    onChange={e => {
                        e.stopPropagation();
                        toggleRowSelected(row.id);
                    }}
                />
            );
        }
        const numericId = Number(row?.id);
        if (Number.isFinite(numericId)) {
            return `AC${String(Math.trunc(numericId)).padStart(3, '0')}`;
        }
        return `AC${String(index + 1).padStart(3, '0')}`;
    };

    const handleDeleteRow = row => {
        setSelectedRows([]);
        setSelectedRowForDelete(row);
        deleteModalRef?.current?.click();
    };

    const totalTableWidth = columnWidths.id + columnWidths.actions + columnOrder.filter(isColumnVisible).reduce((s, c) => s + (columnWidths[c] || 0), 0);

    const getColumnVisibilityOptions = () => [{ value: 'showAll', label: 'Show all' }, ...Object.keys(labelMap).map(k => ({ value: k, label: labelMap[k] }))];

    return (
        <div className={styles.viewHeight}>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-4">
                    <h4 className={styles.pageTitle + ' mb-0'}>Action Log</h4>
                </div>
                <div className="d-flex gap-4 flex-wrap">
                    {selectedRows.length > 0 ? (
                        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginBottom: 8 }}>
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Status <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        {['NOT_STARTED', 'WORKING_ON_IT', 'COMPLETED', 'STUCK'].map(code => (
                                            <MenuItem key={code} onClick={() => dispatch(BulkUpdateActionLogs({ entryIds: selectedRows, updates: { status: code } }))}>
                                                {getStatusInfo(code).text}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <span
                                className={styles.linkText}
                                style={{ cursor: exportCSVLoading ? 'not-allowed' : 'pointer', opacity: exportCSVLoading ? 0.6 : 1 }}
                                onClick={() => !exportCSVLoading && dispatch(ExportActionLogCSV(selectedRows))}>
                                Export ({selectedRows.length})
                            </span>
                            <span role="button" className={styles.linkText} onClick={() => deleteModalRef?.current?.click()}>
                                Delete ({selectedRows.length})
                            </span>
                        </div>
                    ) : (
                        <>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <Filter20Regular className="me-1" />
                                        Filter ({filters.filter(f => f.column && f.value).length})
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: 280 }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex justify-content-between mb-2">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Filter by</h6>
                                                {filters.some(f => f.column && f.value) && (
                                                    <span className={styles.secondaryBtnText} onClick={() => setFilters([{ column: '', value: '', id: Date.now() }])}>
                                                        Clear
                                                    </span>
                                                )}
                                            </div>
                                            {filters.map(f => (
                                                <div key={f.id} className="d-flex gap-2 mb-2 align-items-center">
                                                    <select
                                                        className={'form-select form-select-sm ' + styles.filterSelect}
                                                        value={f.column}
                                                        onChange={e => {
                                                            const v = e.target.value;
                                                            setFilters(prev => prev.map(x => (x.id === f.id ? { ...x, column: v, value: '' } : x)));
                                                        }}>
                                                        <option value="">Column</option>
                                                        {handleFilterColumnOptions().map(o => (
                                                            <option key={o.value} value={o.value}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <select
                                                        className={'form-select form-select-sm ' + styles.filterSelect}
                                                        value={f.value}
                                                        onChange={e => setFilters(prev => prev.map(x => (x.id === f.id ? { ...x, value: e.target.value } : x)))}
                                                        disabled={!f.column}>
                                                        <option value="">Value</option>
                                                        {getFilterValueOptions(f.column).map(o => (
                                                            <option key={o.value} value={o.value}>
                                                                {o.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {filters.length > 1 && (
                                                        <button type="button" className="btn btn-link p-0" onClick={() => setFilters(prev => prev.filter(x => x.id !== f.id))}>
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <span className={styles.secondaryBtnText} onClick={() => setFilters(prev => [...prev, { column: '', value: '', id: Date.now() }])}>
                                                Add filter
                                            </span>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <ArrowSort20Regular className="me-1" />
                                        Sort
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: 280 }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex justify-content-between mb-2">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Sort</h6>
                                                {(sortColumn || sortOrder) && (
                                                    <span
                                                        className={styles.secondaryBtnText}
                                                        onClick={() => {
                                                            setSortColumn('');
                                                            setSortOrder('');
                                                        }}>
                                                        Clear
                                                    </span>
                                                )}
                                            </div>
                                            <div className="d-flex gap-2">
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={sortColumn}
                                                    onChange={e => setSortColumn(e.target.value)}>
                                                    <option value="">Column</option>
                                                    {handleSortColumnOptions().map(o => (
                                                        <option key={o.value} value={o.value}>
                                                            {o.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={sortOrder}
                                                    onChange={e => setSortOrder(e.target.value)}
                                                    disabled={!sortColumn}>
                                                    <option value="">Order</option>
                                                    <option value="asc">Ascending</option>
                                                    <option value="desc">Descending</option>
                                                </select>
                                            </div>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <Grid20Regular className="me-1" />
                                        Group by
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: 280 }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex justify-content-between mb-2">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Group by</h6>
                                                {(groupByColumn || groupBySort) && (
                                                    <span
                                                        className={styles.secondaryBtnText}
                                                        onClick={() => {
                                                            setGroupByColumn('');
                                                            setGroupBySort('');
                                                            setCollapsedGroups(new Set());
                                                        }}>
                                                        Clear
                                                    </span>
                                                )}
                                            </div>
                                            <div className="d-flex gap-2">
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={groupByColumn}
                                                    onChange={e => setGroupByColumn(e.target.value)}>
                                                    <option value="">Column</option>
                                                    <option value="status">Status</option>
                                                    <option value="owner">Owner</option>
                                                    <option value="workstream">Workstream</option>
                                                    <option value="source">Source</option>
                                                </select>
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={groupBySort}
                                                    onChange={e => setGroupBySort(e.target.value)}
                                                    disabled={!groupByColumn}>
                                                    <option value="">Sort groups</option>
                                                    <option value="asc">A–Z</option>
                                                    <option value="desc">Z–A</option>
                                                </select>
                                            </div>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <EyeOff20Regular className="me-1" />
                                        Hide
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: 220 }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <h6 className={styles.tableFilterHeader + ' mb-3'}>Columns</h6>
                                            {getColumnVisibilityOptions().map(opt => (
                                                <div key={opt.value} className="d-flex align-items-center mb-2">
                                                    <input
                                                        type="checkbox"
                                                        style={{ width: 16, height: 16, marginRight: 8, cursor: 'pointer' }}
                                                        checked={opt.value === 'showAll' ? showAllColumns : isColumnVisible(opt.value)}
                                                        onChange={e => handleColumnVisibility(opt.value, e.target.checked)}
                                                    />
                                                    <span className={styles.tableRegularText}>{opt.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <div className="d-flex align-items-center">
                                <button type="button" className={styles.regularSecondaryBtn} onClick={handleAddRowClick}>
                                    Add New Action
                                </button>
                                <Menu>
                                    <MenuTrigger>
                                        <button type="button" className={styles.menuButton}>
                                            <ChevronDown12Regular />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem
                                                style={{ color: 'var(--Brand-Secondary)' }}
                                                icon={<Table20Regular />}
                                                disabled={uploadCSVBtnLoading}
                                                onClick={handleImportActionLogCSV}>
                                                Import Actions from CSV
                                            </MenuItem>
                                            <MenuDivider />
                                            <MenuItem
                                                style={{ color: 'var(--Brand-Secondary)' }}
                                                icon={<ArrowDownload20Regular />}
                                                disabled={downloadTemplateLoading}
                                                onClick={handleDownloadActionLogTemplate}>
                                                Download Template
                                            </MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className={styles.tableContainer} ref={tableContainerRef} style={{ overflow: 'auto' }}>
                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={event => {
                        const { active, over } = event;
                        if (!over || active.id === over.id) return;
                        const oldIndex = columnOrder.indexOf(String(active.id));
                        const newIndex = columnOrder.indexOf(String(over.id));
                        if (oldIndex === -1 || newIndex === -1) return;
                        const next = [...columnOrder];
                        next.splice(oldIndex, 1);
                        next.splice(newIndex, 0, String(active.id));
                        setColumnOrder(next);
                    }}>
                    <table style={{ minWidth: totalTableWidth, width: '100%' }}>
                        <colgroup>
                            <col style={{ width: columnWidths.id }} />
                            {columnOrder.filter(isColumnVisible).map(col => (
                                <col key={col} style={{ width: columnWidths[col] }} />
                            ))}
                            <col style={{ width: columnWidths.actions }} />
                        </colgroup>
                        {!groupByColumn && (
                            <thead>
                                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                                    <tr className={styles.tableHeader}>
                                        <th
                                            className={styles.headerRowItem}
                                            style={{ width: columnWidths.id }}
                                            onMouseEnter={() => setHeaderIdHovered(true)}
                                            onMouseLeave={() => setHeaderIdHovered(false)}>
                                            {headerIdHovered || allVisibleSelected ? (
                                                <input type="checkbox" className={styles.checkboxTable} checked={allVisibleSelected} onChange={handleToggleSelectAllVisible} />
                                            ) : (
                                                <span>ID</span>
                                            )}
                                        </th>
                                        {columnOrder.filter(isColumnVisible).map(col => {
                                            if (col === 'action' || col === 'workstream' || col === 'source') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths[col]}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths[col]}
                                                            minWidth={160}
                                                            maxWidth={800}
                                                            onResize={w => handleColumnResize(col, w)}
                                                            style={{ width: '100%', minWidth: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                                            {labelMap[col]}
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            return (
                                                <SortableHeaderCell key={col} id={col} width={columnWidths[col]}>
                                                    <div>{labelMap[col]}</div>
                                                </SortableHeaderCell>
                                            );
                                        })}
                                        <th className={styles.headerRowItem} style={{ width: columnWidths.actions }} />
                                    </tr>
                                </SortableContext>
                            </thead>
                        )}
                        <tbody>
                            {(() => {
                                const flat = filteredSorted;
                                const grouped = applyGrouping(flat);
                                const rowTpl = (row, index) => (
                                    <tr
                                        key={row.id}
                                        className={styles.tableRow}
                                        onMouseEnter={e => {
                                            setHoveredRowId(row.id);
                                            const el = e.currentTarget.querySelector('.menu-hover-icon');
                                            if (el) el.style.display = 'flex';
                                        }}
                                        onMouseLeave={e => {
                                            setHoveredRowId(null);
                                            const el = e.currentTarget.querySelector('.menu-hover-icon');
                                            if (el) el.style.display = 'none';
                                        }}>
                                        <td className={styles.tableRowItem} style={{ width: columnWidths.id }}>
                                            {renderIdCell(row, index)}
                                        </td>
                                        {columnOrder.filter(isColumnVisible).map(c => renderBodyCell(row, c))}
                                        <td className={styles.tableRowItem} style={{ width: columnWidths.actions }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <div className="menu-hover-icon" style={{ display: 'none' }}>
                                                    <Menu>
                                                        <MenuTrigger>
                                                            <div style={{ cursor: 'pointer' }}>
                                                                <MoreVertical16Regular style={{ width: 16, height: 16 }} />
                                                            </div>
                                                        </MenuTrigger>
                                                        <MenuPopover>
                                                            <MenuList>
                                                                <MenuItem onClick={() => handleDeleteRow(row)}>Delete</MenuItem>
                                                            </MenuList>
                                                        </MenuPopover>
                                                    </Menu>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );

                                if (!grouped) {
                                    const list = flat || [];
                                    const colSpan = 2 + columnOrder.filter(isColumnVisible).length;
                                    if (status === 'loading') {
                                        return (
                                            <tr>
                                                <td colSpan={colSpan}>Loading…</td>
                                            </tr>
                                        );
                                    }

                                    return <>{list.map((row, i) => rowTpl(row, i))}</>;
                                }

                                const visibleColumns = columnOrder.filter(isColumnVisible);
                                const visibleCount = 1 + visibleColumns.length + 1;
                                return grouped.map(group => (
                                    <React.Fragment key={group.groupName}>
                                        <tr>
                                            <td colSpan={visibleCount} style={{ padding: '8px 12px' }}>
                                                <span
                                                    className={styles.todoText}
                                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                                    onClick={() =>
                                                        setCollapsedGroups(prev => {
                                                            const n = new Set(prev);
                                                            if (n.has(group.groupName)) n.delete(group.groupName);
                                                            else n.add(group.groupName);
                                                            return n;
                                                        })
                                                    }>
                                                    <ChevronDown20Regular style={collapsedGroups.has(group.groupName) ? { transform: 'rotate(-90deg)' } : {}} />
                                                    {group.groupName} ({group.items.length})
                                                </span>
                                            </td>
                                        </tr>
                                        {!collapsedGroups.has(group.groupName) && (
                                            <tr className={styles.tableHeader}>
                                                <th className={styles.headerRowItem} style={{ width: columnWidths.id }}>
                                                    ID
                                                </th>
                                                {visibleColumns.map(col => (
                                                    <th key={`${group.groupName}-${col}`} className={styles.headerRowItem} style={{ width: columnWidths[col] }}>
                                                        {labelMap[col]}
                                                    </th>
                                                ))}
                                                <th className={styles.headerRowItem} style={{ width: columnWidths.actions }} />
                                            </tr>
                                        )}
                                        {!collapsedGroups.has(group.groupName) && group.items.map((row, i) => rowTpl(row, i))}
                                    </React.Fragment>
                                ));
                            })()}
                            <tr
                                ref={addRowRef}
                                className={`${styles.tableRow} ${highlightAddRow ? styles.highlightAddRow : ''}`}
                                style={{ backgroundColor: '#F3FDFE', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                <td className={styles.tableRowItem} colSpan={2 + columnOrder.filter(isColumnVisible).length}>
                                    <input
                                        ref={newActionInputRef}
                                        type="text"
                                        className={'form-control form-control-sm ' + styles.taskTextField}
                                        style={{ maxWidth: 480, marginLeft: 52 }}
                                        placeholder="Add new action…"
                                        value={newActionText}
                                        onChange={e => setNewActionText(e.target.value)}
                                        onKeyDown={async e => {
                                            if (e.key === 'Enter') await handleCreateAction();
                                        }}
                                        disabled={buttonLoading}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </DndContext>
            </div>

            {csvFileForMapping && csvHeaders.length > 0 && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.4)' }} role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Map CSV columns to Action Log fields</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCancelCsvImport} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-2" style={{ fontSize: 12, color: '#6c757d' }}>
                                    File: {csvFileForMapping.name}
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-2">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40%' }}>Action Log field</th>
                                                <th>CSV column</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { key: 'action_text', label: 'Action (required)' },
                                                { key: 'owner', label: 'Owner' },
                                                { key: 'due_date', label: 'Due Date' },
                                                { key: 'status', label: 'Status' },
                                                { key: 'workstream_repr', label: 'Workstream' },
                                                { key: 'source_repr', label: 'Source' },
                                            ].map(field => (
                                                <tr key={field.key}>
                                                    <td>{field.label}</td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={csvMapping[field.key] || ''}
                                                            onChange={e => handleChangeCsvMapping(field.key, e.target.value)}>
                                                            <option value="">Not mapped</option>
                                                            {csvHeaders.map(header => (
                                                                <option key={header} value={header}>
                                                                    {header}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCancelCsvImport}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleConfirmCsvImport} disabled={uploadCSVBtnLoading}>
                                    Import CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleActionLogFileUpload} className="d-none" id={csvUploadInputId} disabled={uploadCSVBtnLoading} />

            <DoubleConfirmationModal
                message="Delete selected Action Log entries? This cannot be undone."
                handleAction={() => {
                    if (selectedRowForDelete) {
                        dispatch(DeleteActionLog(selectedRowForDelete.id));
                        setSelectedRowForDelete(null);
                    } else if (selectedRows.length > 0) {
                        dispatch(BulkDeleteActionLogs({ entryIds: selectedRows }));
                        setSelectedRows([]);
                    }
                }}>
                <span ref={deleteModalRef} style={{ display: 'none' }} />
            </DoubleConfirmationModal>

            <ActionLogDetailsSidebar
                open={sidebarOpen}
                onClose={() => {
                    setSidebarOpen(false);
                    setSelectedEntryForSidebar(null);
                }}
                entry={selectedEntryForSidebar}
            />
        </div>
    );
};

export default ActionLog;
